/**
 * Per-Topic OZK 도움연결 config (docs/OZK_FINAL_LAUNCH_STRATEGY.md 4장
 * `helpConnection` contract, 12장 P0.5, 16장 ③A "카톡으로 물어보기").
 *
 * Kept as a separate topicId-keyed lookup - the same pattern already used by
 * topic-problems.ts / topic-guides.ts / topic-content-sources.ts - instead of
 * a field embedded directly in TopicDefinition. Topics are produced through
 * three different code paths (the base OZK_TOPICS array literal, the
 * EXPANDED_LIFE_TOPICS spread, and topic-taxonomy-extensions.ts's push-if-
 * missing side effect), so a lookup keyed by topicId is far less invasive
 * than threading a new field through all three, and matches how every other
 * per-Topic content type in this codebase already works.
 */
import type { HelpConnectionCategoryId, HelpConnectionRiskLevel } from "./help-connection-copy";

export interface TopicHelpConnectionConfig {
  enabled: boolean;
  riskLevel: HelpConnectionRiskLevel;
  /** Empty array means "informational only, no business referral fits" (e.g. a pure gov-service lookup). */
  businessCategories: HelpConnectionCategoryId[];
  requiredCredentials?: string[];
  intakeQuestions: string[];
  safeFirstResponse: string;
  handoffCopy: string;
  autoMatchAllowed: boolean;
  humanReviewRequired: boolean;
}

const R4_DEFAULTS = { autoMatchAllowed: false, humanReviewRequired: true } as const;
const R3_DEFAULTS = { autoMatchAllowed: false, humanReviewRequired: true } as const;
const R2_DEFAULTS = { autoMatchAllowed: false, humanReviewRequired: false } as const;
const R1_DEFAULTS = { autoMatchAllowed: true, humanReviewRequired: false } as const;

export const TOPIC_HELP_CONNECTION: Partial<Record<string, TopicHelpConnectionConfig>> = {
  abn: {
    enabled: true,
    riskLevel: "R2",
    businessCategories: ["accounting_tax"],
    requiredCredentials: ["registered-tax-agent"],
    intakeQuestions: ["어느 지역에 계신가요?", "직원인가요, 계약자(프리랜서)인가요?", "ABN 신청이 이미 거절된 적이 있나요?"],
    safeFirstResponse: "직원인지 계약자인지와 ABN 신청 자격부터 확인하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 회계사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R2_DEFAULTS,
  },
  tfn: {
    enabled: true,
    riskLevel: "R2",
    businessCategories: ["accounting_tax"],
    requiredCredentials: ["registered-tax-agent"],
    intakeQuestions: ["어느 지역에 계신가요?", "취업 시작 전인가요, 이미 근무 중인가요?", "TFN을 신청한 적이 있나요?"],
    safeFirstResponse: "본인 체류 상태에 맞는 ATO 공식 신청 경로를 확인하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 회계사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R2_DEFAULTS,
  },
  "tax-return": {
    enabled: true,
    riskLevel: "R2",
    businessCategories: ["accounting_tax"],
    requiredCredentials: ["registered-tax-agent"],
    intakeQuestions: ["어느 지역에 계신가요?", "올해 소득 형태가 어떻게 되나요(근로소득/사업소득 등)?", "이미 myTax로 신고를 시도해보셨나요?"],
    safeFirstResponse: "소득·공제 자료를 먼저 정리하고 신고 기한을 확인하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 회계사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R2_DEFAULTS,
  },
  "medicare-levy": {
    enabled: true,
    riskLevel: "R2",
    businessCategories: ["accounting_tax"],
    intakeQuestions: ["어느 지역에 계신가요?", "올해 소득이 대략 얼마인가요?", "본인 또는 가족 중 Medicare 자격이 없는 분이 있나요?"],
    safeFirstResponse: "ATO 공식 페이지에서 본인 소득 기준 감면·면제 대상인지부터 확인하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 회계사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R2_DEFAULTS,
  },
  superannuation: {
    enabled: true,
    riskLevel: "R3",
    businessCategories: ["wage_super"],
    intakeQuestions: ["어느 지역에서 근무하시나요?", "근무 기간과 급여 명세서가 있으신가요?", "고용주에게 이미 문의해보셨나요?"],
    safeFirstResponse: "최근 급여 명세서와 근무 기간부터 확인하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 노무 상담사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R3_DEFAULTS,
  },
  "pay-and-wages": {
    enabled: true,
    riskLevel: "R3",
    businessCategories: ["wage_super"],
    intakeQuestions: ["어느 지역에서 근무하시나요?", "근무 기간과 급여 명세서가 있으신가요?", "고용주에게 이미 문의해보셨나요?"],
    safeFirstResponse: "Fair Work 공식 절차부터 확인하고, 근무 기록과 급여 명세서를 정리하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 노무 상담사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R3_DEFAULTS,
  },
  "unfair-dismissal": {
    enabled: true,
    riskLevel: "R4",
    businessCategories: ["legal"],
    requiredCredentials: ["admitted-lawyer"],
    intakeQuestions: ["어느 지역에서 근무하셨나요?", "해고 통보를 받은 날짜가 언제인가요?", "해고 사유를 서면으로 받으셨나요?"],
    safeFirstResponse: "부당해고 신청 기한(통상 21일)부터 확인하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 변호사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R4_DEFAULTS,
  },
  "rental-bond-disputes": {
    enabled: true,
    riskLevel: "R3",
    businessCategories: ["rental"],
    intakeQuestions: ["어느 지역(주)에 계신가요?", "퇴거 후 며칠이 지났나요?", "집주인·중개인과 이미 연락해보셨나요?"],
    safeFirstResponse: "거주 중인 주(state)의 공식 본드 반환 신청 절차부터 확인하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 임대차 상담사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R3_DEFAULTS,
  },
  "rent-increase": {
    enabled: true,
    riskLevel: "R3",
    businessCategories: ["rental"],
    intakeQuestions: ["어느 지역(주)에 계신가요?", "인상 통지를 서면으로 받으셨나요?", "마지막 인상이 언제였나요?"],
    safeFirstResponse: "거주 중인 주(state)의 공식 규정에서 인상 주기·통지 기간부터 확인하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 임대차 상담사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R3_DEFAULTS,
  },
  "rental-repairs-mould": {
    enabled: true,
    riskLevel: "R1",
    businessCategories: ["repair"],
    intakeQuestions: ["어느 지역에 계신가요?", "어떤 수리가 필요하신가요?", "집주인에게 이미 요청하셨나요?"],
    safeFirstResponse: "수리가 필요한 부분과 증상을 먼저 정리하고, 집주인에게 서면으로 요청했는지 확인하세요.",
    handoffCopy: "관련 수리를 도와줄 수 있는 수리 기사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R1_DEFAULTS,
  },
  "eviction-termination": {
    enabled: true,
    riskLevel: "R4",
    businessCategories: ["legal"],
    requiredCredentials: ["admitted-lawyer"],
    intakeQuestions: ["어느 지역(주)에 계신가요?", "종료 통지를 서면으로 받으셨나요?", "통지서에 적힌 종료일이 언제인가요?"],
    safeFirstResponse: "통지 종류와 최소 통지 기간이 지켜졌는지부터 확인하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 변호사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R4_DEFAULTS,
  },
  "share-house": {
    enabled: true,
    riskLevel: "R2",
    businessCategories: ["rental"],
    intakeQuestions: ["어느 지역에 계신가요?", "본인 이름이 계약서에 있나요?", "문제가 룸메이트와의 문제인가요, 집주인과의 문제인가요?"],
    safeFirstResponse: "본인이 계약서(head lease)에 포함되어 있는지부터 확인하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 임대차 상담사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R2_DEFAULTS,
  },
  "australian-bank-account": {
    enabled: true,
    riskLevel: "R2",
    businessCategories: ["finance"],
    intakeQuestions: ["어느 지역에 계신가요?", "호주에 도착한 지 얼마나 되셨나요?", "주소 증명 서류가 있으신가요?"],
    safeFirstResponse: "은행 공식 안내에서 본인 상황에 맞는 신분·주소 증빙부터 비교하세요.",
    handoffCopy: "한국어 상담이 가능한 재정 상담사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R2_DEFAULTS,
  },
  "car-registration": {
    enabled: true,
    riskLevel: "R1",
    businessCategories: ["car"],
    intakeQuestions: ["어느 지역(주)에 계신가요?", "새 차량인가요, 중고 구매/이전인가요?", "등록 만료가 얼마나 남았나요?"],
    safeFirstResponse: "거주 중인 주(state)의 공식 차량 등록 절차와 필요 서류부터 확인하세요.",
    handoffCopy: "한국어로 상담 가능한 카센터가 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R1_DEFAULTS,
  },
  "nsw-driver-licence": {
    enabled: true,
    riskLevel: "R1",
    businessCategories: [],
    intakeQuestions: ["NSW에 정착한 날짜가 언제인가요?", "한국 면허를 아직 가지고 계신가요?"],
    safeFirstResponse: "현재 비자·거주 상태와 NSW 입국·정착 날짜를 확인하세요.",
    handoffCopy: "관련 공식 절차 확인이 우선이라 별도 업체 연결 없이 안내해드릴게요.",
    ...R1_DEFAULTS,
  },
  centrelink: {
    enabled: true,
    riskLevel: "R2",
    businessCategories: ["finance"],
    intakeQuestions: ["어느 지역에 계신가요?", "어떤 급여(payment)를 신청하려 하시나요?", "myGov 계정이 이미 있으신가요?"],
    safeFirstResponse: "본인이 받을 수 있는 급여 종류와 자격 조건부터 확인하세요.",
    handoffCopy: "한국어 상담이 가능한 재정 상담사님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R2_DEFAULTS,
  },
  school: {
    enabled: true,
    riskLevel: "R2",
    businessCategories: ["education"],
    intakeQuestions: ["어느 지역에 계신가요?", "자녀 나이·학년이 어떻게 되나요?", "공립/사립 중 선호가 있으신가요?"],
    safeFirstResponse: "거주 지역의 공립학교 배정 구역(zone)부터 확인하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 유학원·과외 선생님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R2_DEFAULTS,
  },
  "korean-passport": {
    enabled: true,
    riskLevel: "R2",
    businessCategories: ["translation_admin"],
    intakeQuestions: ["어느 지역에 계신가요?", "필요한 업무가 여권 갱신인가요, 다른 영사 업무인가요?", "예약이 필요한 업무인지 확인해보셨나요?"],
    safeFirstResponse: "관할 한국 공관과 필요한 예약·원본 서류를 확인하세요.",
    handoffCopy: "한국어로 상담 가능한 번역·행정대행 담당자가 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R2_DEFAULTS,
  },
  "address-change": {
    enabled: true,
    riskLevel: "R1",
    businessCategories: [],
    intakeQuestions: ["이사 날짜가 언제인가요?", "Centrelink/Medicare 급여를 받고 계신가요?"],
    safeFirstResponse: "myGov 프로필에서 주소를 바꾸고 연결된 ATO·Centrelink·Medicare에 공유할 서비스를 선택하세요.",
    handoffCopy: "정부기관 셀프서비스로 해결되는 항목이라 별도 업체 연결 없이 안내해드릴게요.",
    ...R1_DEFAULTS,
  },
  "visa-status-check": {
    enabled: true,
    riskLevel: "R3",
    businessCategories: ["immigration_visa"],
    requiredCredentials: ["registered-migration-agent"],
    intakeQuestions: ["현재 어떤 비자를 가지고 계신가요(알고 계신 대로)?", "VEVO 조회를 직접 시도해보셨나요?", "곧 만료가 다가오는 상황인가요?"],
    safeFirstResponse: "VEVO(Visa Entitlement Verification Online) 공식 사이트에서 본인 비자 정보를 직접 조회하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 이민 컨설턴트님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R3_DEFAULTS,
  },
  "visa-conditions": {
    enabled: true,
    riskLevel: "R4",
    businessCategories: ["immigration_visa"],
    requiredCredentials: ["registered-migration-agent"],
    intakeQuestions: ["현재 어떤 비자를 가지고 계신가요?", "걱정되는 조건이 근무시간 제한인가요, 다른 조건인가요?", "VEVO 조회를 직접 시도해보셨나요?"],
    safeFirstResponse: "비자 조건을 추측하지 말고 VEVO 공식 사이트에서 본인 비자에 실제로 붙은 조건을 확인하세요.",
    handoffCopy: "이 분야를 한국어로 상담할 수 있는 이민 컨설턴트님이 있습니다. 필요하시면 상담 가능 여부를 확인해드릴게요.",
    ...R4_DEFAULTS,
  },
};

export function getTopicHelpConnection(topicId: string): TopicHelpConnectionConfig | undefined {
  return TOPIC_HELP_CONNECTION[topicId];
}
